import React, { useRef } from "react";
import "tippy.js/dist/tippy.css";
import { getTableColByKey } from "../../lib/utils/stats";
import {
  isTeamWinning,
  losingStyles,
  renderStat,
  setupTooltips,
  winningStyles,
} from "../utils/matchupUtils";
import useMatchupStore from "../store/MatchupStore";

export function HorizontalTeamStatsTable({ formula }) {
  const [
    fixtureTeam1,
    fixtureTeam2,
    showProjStats,
    projTeam1Stats,
    projTeam2Stats,
    actualTeam1Stats,
    actualTeam2Stats,
  ] = useMatchupStore((state) => [
    state.team1,
    state.team2,
    state.showProjStats,
    state.projTeam1Stats,
    state.projTeam2Stats,
    state.actualTeam1Stats,
    state.actualTeam2Stats,
  ]);

  const currentTeam1Stats = showProjStats ? projTeam1Stats : actualTeam1Stats;
  const currentTeam2Stats = showProjStats ? projTeam2Stats : actualTeam2Stats;

  const statCols = formula?.statCols;
  const weights = formula?.values;

  const tableRef = useRef(null);
  setupTooltips(tableRef, [statCols]);

  return (
    <div
      id="team-stats-desktop"
      ref={tableRef}
      className="no-scrollbar mb-2 overflow-x-auto overscroll-x-none border-b border-t bg-white text-xs md:mx-0 md:block md:rounded-md md:border-t-0 md:shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:shadow-none"
    >
      {/* Desktop Layout (Horizontal) */}
      <table className="w-full border-collapse border-spacing-0 md:table">
        <thead className="sticky -top-px z-10 bg-white dark:bg-gray-800 dark:text-gray-300">
          <tr>
            <th className="border-b border-gray-200 py-1.5 pl-2 text-left align-bottom font-medium dark:border-gray-700">
              Team
            </th>
            {statCols.map((stat) => {
              const colInfo = getTableColByKey(stat);
              return (
                <th
                  key={stat}
                  className="border-b border-gray-200 px-0.5 py-1.5 text-center font-medium dark:border-gray-700"
                  data-tippy-content={colInfo?.name || stat}
                >
                  {colInfo?.field || stat}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {/* Team 1 Row */}
          <tr className="bg-white dark:bg-inherit">
            <td className="h-10 min-w-12 border-t border-gray-200 py-1 pl-2 pr-1.5 text-left md:table-cell md:leading-tight dark:border-gray-800">
              <span className="mt-0.5 inline-flex items-center justify-center">
                {/*<span className="mr-1.5 hidden sm:block h-5 w-5">*/}
                {/*  <TeamShield team={fixtureTeam1.teamSeason.team} />*/}
                {/*</span>*/}
                <span className="truncate font-medium">
                  {fixtureTeam1.teamSeason.seasonName}
                </span>
              </span>
            </td>
            {statCols.map((stat) => {
              const team1StatObj = { stats: currentTeam1Stats };
              const team2StatObj = { stats: currentTeam2Stats };
              return (
                <td
                  key={stat}
                  className={`whitespace-nowrap border-t border-gray-200 py-1 px-1.5 text-center dark:border-gray-800 ${
                    isTeamWinning(team1StatObj, team2StatObj, weights, stat)
                      ? winningStyles
                      : losingStyles
                  }`}
                >
                  {renderStat(currentTeam1Stats, stat)}
                </td>
              );
            })}
          </tr>

          {/* Team 2 Row */}
          <tr className="bg-white dark:bg-inherit">
            <td className="h-10 min-w-12 border-t border-gray-200 py-1 pl-2 pr-1.5 text-left md:table-cell md:leading-tight dark:border-gray-800">
              <span className="mt-0.5 inline-flex items-center justify-center">
                {/*<span className="mr-1.5 hidden sm:block h-5 w-5">*/}
                {/*  <TeamShield team={fixtureTeam2.teamSeason.team} />*/}
                {/*</span>*/}
                <span className="truncate font-medium">
                  {fixtureTeam2.teamSeason.seasonName}
                </span>
              </span>
            </td>
            {statCols.map((stat) => {
              const team1StatObj = { stats: currentTeam1Stats };
              const team2StatObj = { stats: currentTeam2Stats };
              return (
                <td
                  key={stat}
                  className={`whitespace-nowrap border-t border-gray-200 py-1 px-1.5 text-center dark:border-gray-800 ${
                    isTeamWinning(team2StatObj, team1StatObj, weights, stat)
                      ? winningStyles
                      : losingStyles
                  }`}
                >
                  {renderStat(currentTeam2Stats, stat)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function MobileTeamStatsTable({ fixtureTeam1, fixtureTeam2, formula }) {
  const statCols = formula?.statCols;
  const weights = formula?.values;

  const tableRef = useRef(null);
  setupTooltips(tableRef, [statCols]);

  return (
    <div
      id="team-stats-mobile"
      ref={tableRef}
      className="no-scrollbar max-w-[100vw] overflow-x-auto overscroll-x-none border-b bg-white text-xs md:mx-0 md:hidden md:rounded-md md:shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:shadow-none"
    >
      {/* Mobile Layout (Vertical) */}
      <table className="w-full table-fixed border-separate border-spacing-0 text-sm md:hidden">
        <tbody>
          {statCols.map((col) => {
            const colInfo = getTableColByKey(col);
            return (
              <tr key={col} className="bg-white dark:bg-inherit">
                <td
                  className={`w-1/2 border-t border-gray-200 px-2 py-1.5 text-right dark:border-gray-800 ${
                    isTeamWinning(fixtureTeam1, fixtureTeam2, weights, col)
                      ? winningStyles
                      : losingStyles
                  }`}
                >
                  {renderStat(fixtureTeam1.stats, col)}
                </td>
                <td
                  className="w-20 border-t border-gray-200 bg-gray-50 py-0.5 text-center text-sm font-medium dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  data-tippy-content={colInfo?.name || col}
                >
                  {colInfo?.field || col}
                </td>
                <td
                  className={`w-1/2 border-t border-gray-200 px-2 py-1.5 text-left dark:border-gray-800 ${
                    isTeamWinning(fixtureTeam2, fixtureTeam1, weights, col)
                      ? winningStyles
                      : losingStyles
                  }`}
                >
                  {renderStat(fixtureTeam2.stats, col)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
